import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type PromotionalBanner = Database['public']['Tables']['promotional_banners']['Row'];
type PromotionalBannerInsert = Database['public']['Tables']['promotional_banners']['Insert'];
type PromotionalBannerUpdate = Database['public']['Tables']['promotional_banners']['Update'];

export function usePromotionalBanners() {
  const queryClient = useQueryClient();

  const { data: banners = [], isLoading, error } = useQuery({
    queryKey: ['promotional-banners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promotional_banners')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PromotionalBanner[];
    },
  });

  const createBanner = useMutation({
    mutationFn: async (banner: PromotionalBannerInsert) => {
      const { data, error } = await supabase
        .from('promotional_banners')
        .insert(banner)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['promotional-banners'] });
      }
    },
  });

  const updateBanner = useMutation({
    mutationFn: async ({ id, ...updates }: PromotionalBannerUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('promotional_banners')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['promotional-banners'] });
      }
    },
  });

  const deleteBanner = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('promotional_banners')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['promotional-banners'] });
      }
    },
  });

  return {
    banners,
    isLoading,
    error,
    createBanner: createBanner.mutateAsync,
    updateBanner: updateBanner.mutateAsync,
    deleteBanner: deleteBanner.mutateAsync,
  };
}

// Hook for getting active banners for a specific page
export function useActiveBanners(pageLocation?: string) {
  try {
    return useQuery({
      queryKey: ['promotional-banners', 'active', pageLocation],
      queryFn: async () => {
        let query = supabase
          .from('promotional_banners')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (pageLocation) {
          query = query.or(`page_location.eq.${pageLocation},page_location.eq.all`);
        }

        const { data, error } = await query;

        if (error) throw error;

        // Filter by date range
        const now = new Date();
        return (data as PromotionalBanner[]).filter(banner => {
        const startDate = banner.start_date ? new Date(banner.start_date) : null;
        const endDate = banner.end_date ? new Date(banner.end_date) : null;
        
        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
        
        return true;
      });
    },
    });
  } catch (e) {
    // QueryClient not available, return empty results
    return { data: [], isLoading: false, error: null } as any;
  }
}
